/** Agenda : vue mensuelle, création/édition d'événements. */
import { el, clear } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { Calendar } from '../api.js';
import { toast } from '../ui/toast.js';
import { openModal, confirmModal } from '../ui/modal.js';

const DOW = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export function renderCalendar(ctx) {
  const { content } = ctx;
  ctx.setCrumbs([{ label: 'Agenda' }]);
  let view = new Date();
  view.setDate(1);
  let events = [];

  const head = el('div', { class: 'cal-head' });
  const grid = el('div', { class: 'cal-grid' });
  content.append(head, grid);

  function gridRange() {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // lundi = 0
    const start = new Date(first); start.setDate(1 - offset);
    const end = new Date(start); end.setDate(start.getDate() + 42);
    return { start, end };
  }

  async function load() {
    const { start, end } = gridRange();
    try { ({ events } = await Calendar.list(start.getTime(), end.getTime())); }
    catch { events = []; }
    paint();
  }

  function buildHead() {
    clear(head);
    const label = el('h2', {}, `${MONTHS[view.getMonth()]} ${view.getFullYear()}`);
    const prev = el('button', { class: 'btn btn-sm btn-icon btn-ghost', html: icon('back', 16), onclick: () => { view.setMonth(view.getMonth() - 1); buildHead(); load(); } });
    const next = el('button', { class: 'btn btn-sm btn-icon btn-ghost', html: icon('chevron', 16), onclick: () => { view.setMonth(view.getMonth() + 1); buildHead(); load(); } });
    const today = el('button', { class: 'btn btn-sm', onclick: () => { view = new Date(); view.setDate(1); buildHead(); load(); } }, "Aujourd'hui");
    const add = el('button', { class: 'btn btn-sm btn-primary', html: icon('plus', 16) + 'Événement', onclick: () => editEvent(null, new Date()) });
    head.append(label, prev, next, today, add);
  }

  function paint() {
    clear(grid);
    DOW.forEach((d) => grid.appendChild(el('div', { class: 'cal-dow' }, d)));
    const { start } = gridRange();
    const now = new Date();
    for (let i = 0; i < 42; i++) {
      const day = new Date(start); day.setDate(start.getDate() + i);
      const dim = day.getMonth() !== view.getMonth();
      const cell = el('div', { class: 'cal-cell' + (dim ? ' dim' : '') + (sameDay(day, now) ? ' today' : '') });
      cell.appendChild(el('span', { class: 'dnum' }, String(day.getDate())));
      events.filter((e) => sameDay(new Date(e.start), day)).slice(0, 4).forEach((e) => {
        const chip = el('div', { class: 'ev', style: e.color ? { background: e.color + '28', color: e.color } : {}, title: e.title },
          (e.all_day ? '' : new Date(e.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) + ' ') + e.title);
        chip.addEventListener('click', (ev) => { ev.stopPropagation(); editEvent(e, day); });
        cell.appendChild(chip);
      });
      cell.addEventListener('click', () => editEvent(null, day));
      grid.appendChild(cell);
    }
  }

  function editEvent(ev, day) {
    openModal((modal, close) => {
      const start = ev ? new Date(ev.start) : (() => { const d = new Date(day); d.setHours(9, 0, 0, 0); return d; })();
      const end = ev ? new Date(ev.end) : (() => { const d = new Date(day); d.setHours(10, 0, 0, 0); return d; })();
      const title = el('input', { class: 'input', placeholder: 'Titre de l’événement', value: ev ? ev.title : '' });
      const date = el('input', { class: 'input', type: 'date', value: ymd(start) });
      const t1 = el('input', { class: 'input', type: 'time', value: `${pad(start.getHours())}:${pad(start.getMinutes())}` });
      const t2 = el('input', { class: 'input', type: 'time', value: `${pad(end.getHours())}:${pad(end.getMinutes())}` });
      const allday = el('input', { type: 'checkbox' }); allday.checked = ev ? !!ev.all_day : false;
      const desc = el('textarea', { class: 'input', placeholder: 'Notes (facultatif)', rows: 3 }, ev ? ev.description : '');

      const times = el('div', { style: { display: 'flex', gap: '8px' } }, t1, t2);
      const toggleTimes = () => { times.style.display = allday.checked ? 'none' : 'flex'; };
      allday.addEventListener('change', toggleTimes); toggleTimes();

      async function save() {
        if (!title.value.trim()) { title.focus(); return; }
        const [y, mo, d] = date.value.split('-').map(Number);
        const mk = (tv, def) => { const [h, mi] = (tv || def).split(':').map(Number); return new Date(y, mo - 1, d, h, mi).getTime(); };
        const payload = {
          title: title.value.trim(), description: desc.value.trim(), all_day: allday.checked,
          start: allday.checked ? new Date(y, mo - 1, d, 0, 0).getTime() : mk(t1.value, '09:00'),
          end: allday.checked ? new Date(y, mo - 1, d, 23, 59).getTime() : mk(t2.value, '10:00'),
        };
        try { ev ? await Calendar.update(ev.id, payload) : await Calendar.create(payload); close(); toast(ev ? 'Événement mis à jour' : 'Événement créé'); load(); }
        catch (e) { toast(e.message, 'err'); }
      }

      modal.append(
        el('h3', {}, ev ? 'Modifier l’événement' : 'Nouvel événement'),
        el('label', {}, 'Titre'), title,
        el('label', {}, 'Date'), date,
        el('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } }, allday, 'Toute la journée'),
        times,
        el('label', {}, 'Notes'), desc,
        el('div', { class: 'row' },
          ev ? el('button', { class: 'btn btn-ghost', style: { marginRight: 'auto', color: '#ff6b6b' }, onclick: async () => { if (await confirmModal({ title: 'Supprimer ?', confirm: 'Supprimer', danger: true })) { await Calendar.remove(ev.id); close(); toast('Supprimé'); load(); } } }, 'Supprimer') : '',
          el('button', { class: 'btn btn-ghost', onclick: close }, 'Annuler'),
          el('button', { class: 'btn btn-primary', onclick: save }, ev ? 'Enregistrer' : 'Créer'))
      );
      title.focus();
    });
  }

  buildHead();
  load();
}
